<template>
  <v-content class="signin_container">
    <div id="recaptcha-container" />

    <!-- Partial: UI Alert -->
    <v-layout justify-center align-center>
      <v-flex style="max-width: 1000px">
        <GlobalUiAlert />
      </v-flex>
    </v-layout>

    <!-- Partial: Content -->
    <v-layout
      justify-center
      column
      align-center
      class="signin_container__content"
    >
      <!-- Container: Loading -->
      <v-card v-if="loading" class="content-container">
        <v-progress-circular
          indeterminate
          size="50"
          class="primary--text ma-5"
        />
      </v-card>

      <!-- Container: Already logged in -->
      <v-card v-else-if="alreadyLoggedIn" class="content-container">
        <v-card-text>{{ alreadyLoggedInMessage }}</v-card-text>
        <v-card-actions>
          <sx-button-group x-align="center" :is-full-width="true">
            <div @click="checkSignIn">
              <sx-button type="primary">Okay</sx-button>
            </div>
          </sx-button-group>
        </v-card-actions>
      </v-card>

      <!-- Container: With custom message -->
      <v-card v-else-if="message" class="content-container text-xs-center">
        <v-card-text class="message"> {{ message }} </v-card-text>
        <v-card-actions>
          <sx-button-group x-align="center" :is-full-width="true">
            <div @click="backToLogin()">
              <sx-button type="secondary">Back</sx-button>
            </div>
          </sx-button-group>
        </v-card-actions>
      </v-card>

      <!-- Container: Form code verification -->
      <v-card v-else-if="showVerificationCodeForm" class="content-container">
        <v-form @submit="submitVerificationCode">
          <v-card-text>
            <div v-if="showVerificationCodeForm === 'sms'" class="text-xs-left">
              We've sent to your phone <b>({{ verificationInfo }})</b> the
              verification code to login. Please note that it may takes up to a
              minute for the code to arrive to your phone.
            </div>
            <div v-else class="text-xs-left">
              We've sent you an email with the code to login. Please make sure
              to check your spam, junk, or filtered inbox if you don't see an
              email within the next minute.
            </div>
            <v-text-field
              name="verificationCode"
              label="Enter verification code"
              type="text"
              v-model="verificationCode"
              :rules="codeRules"
              required
              class="default-input mt-3"
              solo
            >
            </v-text-field>
          </v-card-text>
          <v-card-actions>
            <sx-flex :is-column="true" :is-x-centered="true" :gap="16">
              <sx-button-group x-align="center" :is-full-width="true">
                <div @click="onLoginClick">
                  <sx-button
                    type="primary"
                    :is-disabled="!isVerificationCodeValid"
                    >Login</sx-button
                  >
                </div>
                <div @click="showVerificationCodeForm = false">
                  <sx-button type="secondary">Back</sx-button>
                </div>
              </sx-button-group>
              <sx-separator />
              <sx-button-group x-align="center" :is-full-width="true">
                <div @click="resetSignInMethod()">
                  <sx-button type="secondary" size="small">
                    {{ $t('signin.signInUsingADifferentWay') }}
                  </sx-button>
                </div>
              </sx-button-group>
            </sx-flex>
          </v-card-actions>
        </v-form>
      </v-card>

      <!-- Container: Reauth required -->
      <v-card v-else-if="reauthRequired" class="content-container">
        <v-card-text style="padding: 0px">
          <v-alert
            :value="true"
            outline
            block
            color="info"
            class="text-xs-left"
            style="font-size: 17px"
          >
            Looks like this link has expired. To receive a new link, please
            choose one of the following options below.
          </v-alert>
        </v-card-text>
        <v-list class="mt-2" two-line>
          <!-- State: Token can auth with email -->
          <v-list-tile v-if="!isEmpty(emailAuth)" @click="auth('email')" avatar>
            <v-list-tile-avatar>
              <v-icon class="material-icons-outlined" color="primary"
                >email</v-icon
              >
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title
                class="method-title"
                v-text="'Send a new link to email'"
              ></v-list-tile-title>
              <v-list-tile-sub-title
                v-text="emailAuth.value"
              ></v-list-tile-sub-title>
            </v-list-tile-content>

            <v-list-tile-action>
              <v-icon>chevron_right</v-icon>
            </v-list-tile-action>
          </v-list-tile>

          <!-- State: Token can auth with phone -->
          <v-list-tile v-if="!isEmpty(phoneAuth)" @click="auth('phone')" avatar>
            <v-list-tile-avatar>
              <v-icon class="material-icons-outlined" color="primary"
                >sms</v-icon
              >
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title
                class="method-title"
                v-text="'Send verification code via SMS'"
              ></v-list-tile-title>
              <v-list-tile-sub-title
                v-text="phoneAuth.value"
              ></v-list-tile-sub-title>
            </v-list-tile-content>

            <v-list-tile-action>
              <v-icon>chevron_right</v-icon>
            </v-list-tile-action>
          </v-list-tile>

          <!-- State: No auth method found for the token -->
          <v-list-tile
            v-if="isEmpty(phoneAuth) && isEmpty(emailAuth)"
            @click="onResetLoginMethods()"
            avatar
          >
            <v-list-tile-avatar>
              <v-icon class="material-icons-outlined" color="primary"
                >refresh</v-icon
              >
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title
                class="method-title"
                v-text="'Click here to request a new link'"
              ></v-list-tile-title>
            </v-list-tile-content>

            <v-list-tile-action>
              <v-icon>chevron_right</v-icon>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
      </v-card>

      <!-- Container: Loading with authtoken present -->
      <v-card v-else-if="authToken" class="content-container">
        <v-progress-circular
          indeterminate
          size="50"
          class="primary--text ma-5"
        />
      </v-card>

      <!-- Container: Show 2fa auth -->
      <v-card v-else-if="show2FAForm" class="content-container text-center">
        <v-card-title class="title pt-4"> Security Verification </v-card-title>
        <v-card-text v-if="sendingCode">
          <v-layout>
            <v-flex xs12>
              <v-progress-circular
                indeterminate
                size="40"
                class="primary--text mb-2"
              />
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex xs12> Sending verification... </v-flex>
          </v-layout>
        </v-card-text>
        <v-form
          v-else
          v-model="formCodeValid"
          ref="codeForm"
          @submit="validateCode"
        >
          <v-card-text>
            <v-alert :value="!!codeErrorMessage" type="error">
              {{ codeErrorMessage }}
              <v-btn :loading="sendingCode" flat @click="doVerify($event)">
                Resend code
              </v-btn>
            </v-alert>
            <sx-flex :is-x-centered="true">
              <sx-emblem icon-name="verified" />
            </sx-flex>
            <v-text-field
              name="verificationCode"
              label="Enter verification code"
              type="text"
              v-model="code"
              required
              class="default-input mt-3"
              solo
            >
            </v-text-field>
          </v-card-text>
          <v-card-actions>
            <sx-button-group x-align="center" :is-full-width="true">
              <div @click="message = null">
                <sx-button
                  type="primary"
                  :is-loading="validatingCode"
                  :is-disabled="!code"
                  >Verify</sx-button
                >
              </div>
            </sx-button-group>
          </v-card-actions>
        </v-form>
      </v-card>

      <!-- Layout: With sign-in image on external domains -->
      <v-layout v-else-if="showSignInImage === true">
        <v-flex xs-12 md6 class="left-container">
          <v-layout
            v-if="firmLogo"
            align-center
            justify-center
            class="px-3 pt-4 logo-container"
          >
            <img :src="firmLogo" class="logo" />
          </v-layout>
          <v-layout column class="sign-in-form mt-5 px-3">
            <h5 class="title pt-4">
              {{ $t('signin.signIn') }}
            </h5>
            <v-form ref="form" v-model="valid" class="mt-4">
              <div class="pb-0 pt-1">
                <div class="text-xs-left">
                  <span>{{ $t('signin.email') }}</span>
                </div>
                <v-text-field
                  name="username"
                  :placeholder="
                    signInWithPhoneNumberEnabled
                      ? $t('signin.enterYourEmailOrPhoneNumberHere')
                      : $t('signin.enterYourEmailHere')
                  "
                  type="text"
                  v-model="$v.username.$model"
                  :error="$v.username.$error"
                  :error-messages="$v.username.$dirty ? usernameErrors : []"
                  :disabled="showPasswordInput"
                  class="mt-1 default-input"
                  :class="{ pointer: showPasswordInput }"
                  solo
                >
                  <template v-if="showPasswordInput" v-slot:append>
                    <v-btn
                      flat
                      icon
                      color="primary"
                      @click="showPasswordInput = false"
                    >
                      <v-icon>mode_edit</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
                <div v-if="showPasswordInput">
                  <div class="text-xs-left">
                    {{ $t('signin.password') }}
                  </div>
                  <v-text-field
                    name="password"
                    :label="$t('signin.enterYourPasswordHere')"
                    id="password"
                    type="password"
                    required
                    v-model="password"
                    :rules="passwordRules"
                    class="mt-1 default-input"
                    :hint="
                      retryCount >= 5
                        ? $t('signin.forgotPasswordHelp')
                        : undefined
                    "
                    :persistent-hint="true"
                    solo
                  />
                </div>
              </div>

              <v-card-actions>
                <sx-flex :is-column="true" :gap="16">
                  <sx-button-group x-align="center" :is-full-width="true">
                    <sx-button
                      type="primary"
                      :is-loading="isSubmitLoading"
                      :is-disabled="!valid"
                    >
                      <span v-if="showPasswordInput">{{
                        $t('signin.login')
                      }}</span>
                      <span v-else>{{ $t('signin.next') }}</span>
                    </sx-button>
                  </sx-button-group>
                  <sx-separator />
                  <sx-button-group x-align="center" :is-full-width="true">
                    <div
                      v-if="showPasswordInput && isEmail(username)"
                      @click="
                        $router.push(
                          `/reset-password?email=${encodeURIComponent(
                            email || '',
                          )}`,
                        )
                      "
                    >
                      <sx-button type="secondary" size="small">
                        {{ $t('signin.forgotPassword') }}
                      </sx-button>
                    </div>

                    <div v-if="showPasswordInput" @click="resetSignInMethod()">
                      <sx-button type="secondary" size="small">
                        {{ $t('signin.signInUsingADifferentWay') }}
                      </sx-button>
                    </div>
                  </sx-button-group></sx-flex
                >

                <div class="text-xs-center">
                  <span class="grey--text darken-2">
                    If you have any issues, please contact hello@soraban.com.
                  </span>
                </div>
              </v-card-actions>
            </v-form>
          </v-layout>
        </v-flex>

        <v-flex md6 class="right-container hidden-sm-and-down">
          <img class="sign-in-image" src="@/assets/sign-in-image.png" />
        </v-flex>
      </v-layout>

      <!-- Layout: Without sign-in image -->
      <v-card class="content-container" v-else>
        <v-layout
          v-if="firmLogo"
          align-center
          justify-center
          class="px-3 pt-2 logo-container"
        >
          <img :src="firmLogo" class="logo" />
        </v-layout>
        <h5 class="title pt-4 text-xs-center">
          <span>{{ $t('signin.signIn') }}</span>
        </h5>
        <v-form ref="form" v-model="valid" @submit="submit" class="mt-4">
          <div class="pb-0 pt-1">
            <div class="text-xs-left">
              <span v-if="signInWithPhoneNumberEnabled"
                >Email or Phone Number</span
              >
              <span v-else>Email</span>
            </div>
            <v-text-field
              name="username"
              :placeholder="
                signInWithPhoneNumberEnabled
                  ? $t('signin.enterYourEmailOrPhoneNumberHere')
                  : $t('signin.enterYourEmailHere')
              "
              type="text"
              v-model="$v.username.$model"
              :error="$v.username.$error"
              :error-messages="$v.username.$dirty ? usernameErrors : []"
              :disabled="showPasswordInput"
              class="mt-1 default-input"
              :class="{ pointer: showPasswordInput }"
              solo
            >
              <template v-if="showPasswordInput" v-slot:append>
                <v-btn
                  flat
                  icon
                  color="primary"
                  @click="showPasswordInput = false"
                >
                  <v-icon>mode_edit</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <div v-if="showPasswordInput">
              <div class="text-xs-left">
                {{ $t('signin.password') }}
              </div>
              <v-text-field
                name="password"
                :label="$t('signin.enterYourPasswordHere')"
                id="password"
                type="password"
                required
                v-model="password"
                :rules="passwordRules"
                class="mt-1 default-input"
                :hint="
                  retryCount >= 5 ? $t('signin.forgotPasswordHelp') : undefined
                "
                :persistent-hint="true"
                solo
              />
            </div>
          </div>

          <v-card-actions>
            <sx-flex :is-column="true" :gap="16">
              <sx-button-group x-align="center" :is-full-width="true">
                <div>
                  <sx-button
                    type="primary"
                    :is-loading="isSubmitLoading"
                    :is-disabled="!valid || $v.username.$error"
                  >
                    <span v-if="showPasswordInput">{{
                      $t('signin.login')
                    }}</span>
                    <span v-else>{{ $t('signin.next') }}</span>
                  </sx-button>
                </div>
              </sx-button-group>
              <div v-if="showPasswordInput">
                <sx-separator />
              </div>
              <div v-if="showPasswordInput">
                <sx-button-group x-align="center" :is-full-width="true">
                  <div
                    v-if="showPasswordInput && isEmail(username)"
                    @click="
                      $router.push(
                        `/reset-password?email=${encodeURIComponent(
                          email || '',
                        )}`,
                      )
                    "
                  >
                    <sx-button type="secondary" size="small">{{
                      $t('signin.forgotPassword')
                    }}</sx-button>
                  </div>
                  <div
                    v-if="showPasswordInput"
                    @click.prevent="resetSignInMethod()"
                  >
                    <sx-button type="secondary" size="small">
                      {{ $t('signin.signInUsingADifferentWay') }}
                    </sx-button>
                  </div>
                </sx-button-group>
              </div>
            </sx-flex>
          </v-card-actions>
        </v-form>
      </v-card>

      <!-- Partial: Helper -->
      <sc-flex :is-y-centered="true">
        <sc-small :is-secondary="true" align="center">
          Having trouble logging in?<br />
          Visit <a @click="$router.push(`/help`)">Help Center</a> for
          assistance.
        </sc-small>
      </sc-flex>
    </v-layout>
  </v-content>
</template>

<script>
import GlobalUiAlert from '@/components/GlobalUiAlert';
import firebase from '@/firebase';
import { mapGetters } from 'vuex';
import jwt_decode from 'jwt-decode';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import get from 'lodash/get';
import { getSubdomain } from 'tldjs';
import { required } from 'vuelidate/lib/validators';
import SxButton from '@/components/sx/SxButton.vue';
import SxButtonGroup from '@/components/sx/SxButtonGroup.vue';
import SxEmblem from '@/components/sx/SxEmblem.vue';
import SxFlex from '@/components/sx/SxFlex.vue';
import SxSeparator from '@/components/sx/SxSeparator.vue';

export default {
  name: 'Signin',
  data() {
    return {
      alreadyLoggedIn: false,
      alreadyLoggedInMessage: '',
      authMethods: [],
      code: null,
      codeErrorMessage: null,
      firmLogo: null,
      formCodeValid: false,
      isSubmitLoading: false,
      loading: false,
      message: null,
      password: '',
      phoneNumber: null,
      recaptchaVerifier: null,
      retryCount: 0,
      sendingCode: false,
      show2FAForm: false,
      showPasswordInput: false,
      showSignInImage: false,
      showVerificationCodeForm: false,
      signInWithPhoneNumberEnabled: false,
      uid: null,
      username: null,
      valid: false,
      validatingCode: false,
      verificationCode: '',
      verificationInfo: null,
      // NOTE @chris Move this to mixin later
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 6 || 'Password must be greater than 6 characters',
      ],
      // NOTE @chris Move this to mixin later
      codeRules: [
        v => !!v || 'Verification code is required',
        v => (v && v.length === 6) || 'Verification code contains 6 digits',
      ],
    };
  },
  validations: {
    username: {
      required,
    },
  },
  components: {
    GlobalUiAlert,
    SxEmblem,
    SxFlex,
    SxButton,
    SxButtonGroup,
    SxSeparator,
  },
  computed: {
    ...mapGetters({
      loginLoading: 'loginLoading',
      reauthRequired: 'reauthRequired',
      currentUser: 'user',
    }),
    usernameErrors() {
      if (this.$v.username.required) return [];

      if (this.signInWithPhoneNumberEnabled) {
        return [this.$t('rules.emailOrPhoneNumberPresence')];
      } else {
        return [this.$t('rules.emailPresence')];
      }
    },
    authToken() {
      const params = new URLSearchParams(window.location.search);
      return params.get('auth_token');
    },
    emailAuth() {
      return this.authMethods.find(method => method.name === 'email');
    },
    phoneAuth() {
      return this.authMethods.find(method => method.name === 'phone');
    },
    slug() {
      return getSubdomain(window.location.href);
    },
    isVerificationCodeValid() {
      return Boolean(
        this.verificationCode && this.verificationCode.length === 6,
      );
    },
  },
  methods: {
    // NOTE @chris Move this to mixin later
    isEmpty,
    // NOTE @chris Move this to mixin later
    isEmail(value) {
      return /.+@.+/.test(value);
    },

    // Resets back to login input
    backToLogin() {
      this.message = null;
      this.$store.dispatch('setLoginLoading', false);
    },

    // Fallback method to restart login flow without states when no authMethod was found
    onResetLoginMethods() {
      window.location.assign('/');
    },

    onLoginClick(event) {
      event && event.preventDefault && event.preventDefault();
      event && event.stopPropagation && event.stopPropagation();

      this.message = null;
      this.$store.dispatch('setLoginLoading', true);
      this.submitVerificationCode();
    },

    // Starts sign in method reset
    async resetSignInMethod(event) {
      event && event.stopPropagation && event.stopPropagation();
      event && event.preventDefault && event.preventDefault();

      try {
        this.showPassword = false;
        const { data } = isEmpty(this.uid)
          ? await API.user.changeLoginMethod({ username: this.username.trim() })
          : await API.user.changeLoginMethod({ uid: this.uid });

        // Sent sms
        if (data.method === 'sent-sms-code') {
          this.verificationInfo = `*** - *** - ${data.phone}`;
          this.showVerificationCodeForm = 'sms';
        }
        // Sent email link
        else if (data.method === 'sent-email-link') {
          this.message = `Sent an email to ${data.email} with a link to reset your sign-in method.`;
          this.showVerificationCodeForm = false;
        }
      } catch (error) {
        console.error(error);
        const { message } = error && error.response && error.response.data;
        this.$store.dispatch('application/setErrorNotification', {
          message:
            message ||
            'There was something wrong. Please refresh the page and try again.',
        });
      }
    },

    // Submit login form
    async submit(event) {
      event && event.preventDefault && event.preventDefault();
      event && event.stopPropagation && event.stopPropagation();

      await this.$v.username.$touch();

      // Do nothing with error present
      if (this.$v.username.$error)
        return this.$store.dispatch('setLoginLoading', false);

      this.$store.dispatch('setLoginLoading', true);
      this.isSubmitLoading = true;

      // With password input
      if (this.showPasswordInput && this.password) {
        try {
          // Try WorkOS authentication first
          try {
            const workosResponse = await this.verifyPasswordWithWorkOS(
              this.username.trim(),
              this.password,
            );

            if (workosResponse.success && workosResponse.requires_2fa) {
              // Handle 2FA flow
              this.uid = workosResponse.uid;
              this.show2FAForm = true;
              this.sendingCode = true;

              try {
                await API.auth.sendEmailVerificationCode({
                  uid: workosResponse.uid,
                });
                this.sendingCode = false;
              } catch (error) {
                this.sendingCode = false;
                this.$store.dispatch('application/setErrorNotification', {
                  message: 'Error sending verification code, please try again.',
                });
              }
              return;
            } else if (
              workosResponse.success &&
              workosResponse.auth_token &&
              workosResponse.user
            ) {
              // Direct WorkOS login
              return this.signInWithDirectToken(
                workosResponse.auth_token,
                workosResponse.user,
              );
            }
          } catch (workosError) {
            // Fall back to Firebase if WorkOS fails
          }

          // Firebase authentication fallback
          const firebaseToken = await this.verifyPasswordForFirebaseToken({
            username: this.username.trim(),
            password: this.password,
          });

          const response = await this.$store.dispatch('userLogin', {
            authToken: firebaseToken,
            redirectPath: this.$route.query.redirect,
          });

          if (response === null) {
            this.retryCount += 1;
          } else {
            this.show2FAForm = response === 'REQUEST_2FA';

            if (this.show2FAForm) {
              this.sendingCode = true;
              this.phoneNumber = `+1${this.currentUser.phone_number}`;
              this.doVerify(false);
            }
          }
        } catch (error) {
          this.retryCount += 1;
          const errorMessage = has(error, 'response.data.error')
            ? this.$t(`signin.error.${get(error, 'response.data.error')}`)
            : has(error, 'response.data.message')
            ? get(error, 'response.data.message')
            : this.$t('signin.error.default');

          this.$store.dispatch('application/setErrorNotification', {
            message: errorMessage,
          });
          this.$store.commit('setLoginLoading', false);
        }
      }
      // With email only
      else {
        try {
          const { data: account } = await API.auth.getPreferredSignInMethod({
            username: this.username.trim(),
          });
          this.uid = account.uid;

          if (account) {
            if (account.passwordless) {
              const {
                data: { message },
              } = await API.auth.sendAuthLink({
                uid: account.uid,
                accountantId: account.accountant_id,
              });

              this.message = message;
            } else {
              if (account.preferred_sign_in_method === 'password') {
                this.showPasswordInput = true;
              } else if (account.preferred_sign_in_method === 'sms') {
                this.loading = true;
                this.uid = account.uid;

                try {
                  const phoneVerificationCode = await API.auth.sendSMSVerificationCode(
                    {
                      uid: account.uid,
                      redirect: this.$route.query.redirect,
                    },
                  );

                  this.verificationInfo = `*** - *** - ${phoneVerificationCode.data.phone}`;
                  this.showVerificationCodeForm = 'sms';
                } catch (error) {
                  const { message } =
                    error && error.response && error.response.data;
                  this.$store.dispatch('application/setErrorNotification', {
                    message:
                      message ||
                      'There was something wrong. Please refresh the page and try again.',
                  });
                } finally {
                  this.loading = false;
                }
              } else {
                this.loading = true;
                this.uid = account.uid;

                try {
                  const emailVerificationCode = await API.auth.sendEmailVerificationCode(
                    {
                      uid: account.uid,
                      redirect: this.$route.query.redirect,
                    },
                  );

                  // Show appropriate messaging based on code type
                  if (emailVerificationCode.data.workos_passcode) {
                    this.verificationInfo = `${emailVerificationCode.data.email} (6-digit passcode)`;
                  } else {
                    this.verificationInfo = `${emailVerificationCode.data.email} (6-character code)`;
                  }
                  this.showVerificationCodeForm = 'email';
                } catch (error) {
                  const { message } =
                    error && error.response && error.response.data;
                  this.$store.dispatch('application/setErrorNotification', {
                    message:
                      message ||
                      'There was something wrong. Please refresh the page and try again.',
                  });
                } finally {
                  this.loading = false;
                }
              }
            }
          } else {
            const errorMessage = this.signInWithPhoneNumberEnabled
              ? 'Email or phone number is not registered.'
              : 'Email is not registered.';

            this.$store.dispatch('application/setErrorNotification', {
              message: errorMessage,
            });
          }
        } catch (error) {
          console.error(error);
          const errorMessage = this.signInWithPhoneNumberEnabled
            ? 'Email or phone number is not correct.'
            : 'Email is not correct.';

          this.$store.dispatch('application/setErrorNotification', {
            message: errorMessage,
          });
        }
      }

      this.isSubmitLoading = false;
    },

    // Verify a firebase token
    async verifyPasswordForFirebaseToken(event) {
      event && event.preventDefault && event.preventDefault();
      event && event.stopPropagation && event.stopPropagation();

      const tokenAttributes = {
        username: this.username.trim(),
        password: this.password,
      };

      const response = await API.auth.verifyPasswordForFirebaseToken(
        tokenAttributes,
      );

      return response.data.firebase_token;
    },

    // Verify password with WorkOS
    async verifyPasswordWithWorkOS(username, password) {
      const response = await API.auth.verifyPasswordWithWorkOS({
        username: username.trim(),
        password,
      });

      return response.data;
    },

    // Submits a verification code. Could come from sms, email or 2fa
    async submitVerificationCode(event) {
      event && event.preventDefault && event.preventDefault();
      event && event.stopPropagation && event.stopPropagation();

      if (!this.isVerificationCodeValid) return;

      try {
        this.$store.dispatch('setLoginLoading', true);

        const { data } = await API.auth.verifyVerificationCode({
          uid: this.uid,
          verificationCode: this.verificationCode,
        });

        if (data.success && data.user && data.auth_token) {
          // Direct WorkOS/Bearer token flow
          return this.signInWithDirectToken(data.auth_token, data.user);
        } else if (data.auth_token) {
          // Legacy Firebase flow (fallback)
          const firebaseToken = await this.exchangeForFirebaseToken(
            data.auth_token,
          );
          return this.signInWithToken(firebaseToken);
        }
      } catch (error) {
        const errorMessage = has(error, 'response.data.error')
          ? get(error, 'response.data.error')
          : has(error, 'response.data.message')
          ? get(error, 'response.data.message')
          : 'Something went wrong. Please try again later.';

        this.$store.dispatch('setLoginLoading', false);
        this.$store.dispatch('application/setErrorNotification', {
          message: errorMessage,
          errorToSend: error,
        });
      }
    },

    // Checks if user was already logged in
    async checkSignIn(event) {
      event && event.preventDefault && event.preventDefault();
      event && event.stopPropagation && event.stopPropagation();

      if (!isEmpty(this.currentUser)) {
        this.alreadyLoggedIn = false;
        await this.signOut();
      }

      try {
        // Use the same pattern as React app
        const response = await API.auth.authenticateWithSorabanToken({
          sorabanToken: this.authToken,
        });

        if (response.data.success && response.data.user) {
          // Direct Bearer token authentication (like React)
          await this.signInWithDirectToken(this.authToken, response.data.user);
        } else if (response.data.error) {
          this.$store.commit('setReauthRequired', true);
          await this.getAuthMethods();
        }
      } catch (error) {
        this.$store.commit('setLoginLoading', false);
        this.$store.commit('setReauthRequired', true);
        await this.getAuthMethods();
      }
    },

    // Exchange soraban token for a firebase one
    async exchangeForFirebaseToken(token) {
      this.$store.dispatch('setLoginLoading', true);

      const response = await API.auth.exchangeForFirebaseToken({
        soraban_token: token,
      });

      return response.data.firebase_token;
    },

    // Login with a firebase token, redirect when this is a 2fa state
    async signInWithToken(firebaseToken) {
      this.$store.dispatch('setLoginLoading', false);

      // Store the Firebase token in localStorage for new getToken method
      localStorage.setItem('firebase__authToken', firebaseToken);

      const redirect = this.$route.query.redirect || '/';

      await this.$store.dispatch('userLogin', {
        authToken: firebaseToken,
        redirectPath: redirect,
        isAuthenticated: true,
      });
    },

    // Login with direct Bearer token (WorkOS style)
    async signInWithDirectToken(authToken, user) {
      this.$store.dispatch('setLoginLoading', false);

      const bearerToken = `Bearer ${authToken}`;
      localStorage.setItem('firebase__authToken', bearerToken);

      // Set user and token directly
      this.$store.commit('setUser', user);
      this.$store.commit('setToken', bearerToken);
      this.$store.commit('setReauthRequired', false);

      await this.$store.dispatch('setCurrentFirm');

      // Handle redirect
      const redirect = this.$route.query.redirect || '/';
      await this.$router.push(redirect);
    },

    // Sign out
    async signOut() {
      this.$store.dispatch('setLoginLoading', true);
      this.uid = null;
      this.retryCount = 0;
      this.authToken = null;

      await firebase.auth().signOut();
      await this.$store.dispatch('userSignOutSilently');
    },

    // Get auth methods for the user
    async getAuthMethods() {
      try {
        const {
          data: { methods },
        } = await API.auth.getAuthMethods({ uid: this.uid });

        this.authMethods = methods;
      } catch (error) {
        console.error("can't get auth methods", { error });
      }
    },

    // Authenticate
    async auth(method) {
      const decoded = jwt_decode(this.authToken);
      const decodedUid = decoded.uid;
      const accountantId = decoded.accountant_id;
      const partnerId = decoded.partner_id;
      const redirect = this.$route.query.redirect;

      this.loading = true;

      if (method === 'email') {
        const {
          data: { message },
        } = await API.auth.sendAuthLink({
          uid: decodedUid,
          accountantId,
          partnerId,
          redirect,
        });
        this.message = message;
        this.loading = false;
      } else if (method === 'phone') {
        try {
          const phoneVerificationCode = await API.auth.sendSMSVerificationCode({
            uid: decodedUid,
            redirect,
          });

          this.verificationInfo = `*** - *** - ${phoneVerificationCode.data.phone}`;
          this.showVerificationCodeForm = 'sms';
        } catch (error) {
          const { message } = error && error.response && error.response.data;
          this.$store.dispatch('application/setErrorNotification', {
            message:
              message ||
              'There was something wrong. Please refresh the page and try again.',
          });
        } finally {
          this.loading = false;
        }
      } else {
        console.error('auth method not supported', { method });
      }
    },

    // Check phone sign on external domain
    async checkSignInWithPhoneNumber() {
      const response = await API.firm.isSignInWithPhoneNumberEnabled(
        this.slug,
        window.location.host,
      );
      const { enabled } = response.data;
      this.signInWithPhoneNumberEnabled = enabled;
    },

    // Check logo from slug on external domain
    async getFirmLogoBySlug() {
      const response = await API.firm.getFirmLogoBySlug(
        this.slug,
        window.location.host,
      );
      const { url } = response.data;
      if (!(url && url.includes('missing.png'))) {
        this.firmLogo = response.data.url;
      }
    },

    // Check sign in image on external domain
    async showSignInImageBySlug() {
      const response = await API.firm.showSignInImageBySlug(
        this.slug,
        window.location.host,
      );
      this.showSignInImage = response.data.show_sign_in_image;
    },

    // Verify code
    async doVerify() {
      this.sendingCode = true;

      try {
        const { data } = await API.auth.getPreferredSignInMethod({
          username: this.username.trim(),
        });
        this.uid = data.uid;

        await API.auth.send2FALink({
          uid: data.uid,
        });
      } catch (error) {
        const errorMessage = has(error, 'response.data.error')
          ? get(error, 'response.data.error')
          : has(error, 'response.data.message')
          ? get(error, 'response.data.message')
          : 'Something went wrong. Please try again later.';

        this.$store.dispatch('application/setErrorNotification', {
          message:
            errorMessage ||
            'Error sending verification code, please check your phone number and try again.',
        });
      } finally {
        this.sendingCode = false;
        await this.$store.dispatch('userSignOutSilently');
      }
    },

    // Submit validate code form
    async validateCode(event) {
      event && event.preventDefault && event.preventDefault();

      try {
        this.validatingCode = true;
        const { data } = await API.auth.verifyVerificationCode({
          uid: this.uid,
          verificationCode: this.code,
        });

        if (data.success && data.user && data.auth_token) {
          // Direct WorkOS/Bearer token flow
          return this.signInWithDirectToken(data.auth_token, data.user);
        } else if (data.auth_token) {
          // Legacy Firebase flow (fallback)
          const firebaseToken = await this.exchangeForFirebaseToken(
            data.auth_token,
          );
          return this.signInWithToken(firebaseToken);
        }
      } catch (error) {
        const errorMessage = has(error, 'response.data.error')
          ? get(error, 'response.data.error')
          : has(error, 'response.data.message')
          ? get(error, 'response.data.message')
          : 'Something went wrong. Please try again later.';

        if (errorMessage) {
          this.$store.dispatch('application/setErrorNotification', {
            message: errorMessage,
            errorToSend: error,
          });
        } else {
          this.codeErrorMessage = error.message;
        }
        this.validatingCode = false;
      }
    },
  },
  mounted() {
    this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      'recaptcha-container',
      {
        size: 'invisible',
      },
    );
  },
  async created() {
    const isExternal = window.location.host !== 'app.soraban.com';

    if (this.$route.query.email) {
      this.$store.dispatch('setLoginLoading', true);
      this.username = decodeURIComponent(this.$route.query.email);

      const response = await API.auth.getPreferredSignInMethod({
        username: this.username,
      });
      const account = response.data;
      if (account.preferred_sign_in_method === 'password') {
        this.showPasswordInput = true;
      }
      this.$store.dispatch('setLoginLoading', false);
    }

    if (isExternal) {
      this.checkSignInWithPhoneNumber();
      this.getFirmLogoBySlug();
      this.showSignInImageBySlug();
    }

    if (this.authToken) {
      const decoded = jwt_decode(this.authToken);
      this.uid = decoded.uid;
      this.loading = true;

      if (!isEmpty(firebase.auth().currentUser)) {
        if (firebase.auth().currentUser.uid !== this.uid) {
          if (this.currentUser && this.currentUser.is_advisor) {
            this.alreadyLoggedInMessage =
              'You are trying to log in as a client. This will sign you out as an accountant.';
          } else {
            this.alreadyLoggedInMessage =
              "You're signed in under a different account than the one registered with the email. You will be logged out and need to login again to access this account.";
          }

          this.alreadyLoggedIn = true;
        } else {
          if (this.$route.query.redirect) {
            this.$router.replace(this.$route.query.redirect);
          } else {
            this.$router.replace('/');
          }
        }
      } else {
        await this.checkSignIn();
      }

      this.loading = false;
    }
  },
};
</script>

<style scoped="true" lang="css">
.signin_container {
  font-family: var(--font-family-sans);
}

.signin_container__content {
  min-height: 90vh;
  gap: 16px;
}

.left-container {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100vh;
  justify-content: center;

  background-color: var(--gray-0);
}

.right-container {
  border-left: 1px solid var(--gray-100);
}

.logo-container {
  flex: 0;
}

.sign-in-form {
  flex: 0;
  text-align: left;
}

.sign-in-image {
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.pointer {
  pointer-events: initial;
}

.v-toolbar {
  background-color: #46535c;
}

.content-container {
  width: 400px;
  padding: 24px;
  margin-left: 16px;
  margin-right: 16px;
}

.title {
  justify-content: center;

  font-size: 24px !important;
  font-weight: bold;
}
.message {
  font-size: 16px;
}

.d-flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}
</style>
